import React, { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

/**
 * Shared "You're in" success splash used by both auth landing pages
 * (`AuthVerify` for magic-link and `AuthGoogleCallback` for Google). Big
 * emerald checkmark, staggered fade-in, subtle pulsing halo behind. Sits
 * on-screen long enough for the user to register that auth actually
 * succeeded before the parent navigates them away.
 *
 * Parent owns the redirect - this component just shows the splash and
 * calls onDone after the display window elapses.
 */
const AuthSuccessSplash = ({
  headline = "You're in.",
  subline = 'One tap away from your account.',
  email,
  onDone,
  durationMs = 700,
  testId = 'auth-success-splash',
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    const t = setTimeout(() => onDone && onDone(), durationMs);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [durationMs, onDone]);

  return (
    <div
      data-testid={testId}
      className="min-h-screen bg-black text-white flex items-center justify-center px-4"
      role="status"
      aria-live="polite"
    >
      <div className={`text-center transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
        {/* Pulsing green halo behind the tick */}
        <div className="relative inline-flex items-center justify-center mb-6">
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[splash-pulse_1.4s_ease-out_infinite]"
            style={{ width: 140, height: 140, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
          />
          <span
            aria-hidden
            className="absolute rounded-full bg-emerald-500/15"
            style={{ width: 180, height: 180, left: '50%', top: '50%', transform: 'translate(-50%, -50%)', filter: 'blur(24px)' }}
          />
          <CheckCircle2
            className="relative h-24 w-24 text-emerald-400 drop-shadow-[0_0_18px_rgba(52,211,153,0.7)]"
            strokeWidth={2.2}
          />
        </div>

        <p className="text-emerald-400 text-[11px] font-black uppercase tracking-[0.4em] mb-3">Signed in</p>
        <h1
          className="text-4xl sm:text-5xl font-black leading-tight"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          data-testid={`${testId}-headline`}
        >
          {headline}
        </h1>
        {email && (
          <p className="mt-2 text-sm text-zinc-300 font-mono truncate max-w-xs mx-auto" title={email}>
            {email}
          </p>
        )}
        <p className="mt-3 text-zinc-400 text-sm">{subline}</p>

        {/* Progress bar so users see the redirect coming */}
        <div className="mt-6 w-40 mx-auto h-0.5 bg-zinc-800 rounded-full overflow-hidden">
          <span
            className="block h-full bg-emerald-400"
            style={{ animation: `splash-fill ${durationMs}ms linear forwards` }}
          />
        </div>
      </div>

      <style>{`
        @keyframes splash-pulse {
          0%   { transform: translate(-50%, -50%) scale(0.85); opacity: 0.55; }
          50%  { transform: translate(-50%, -50%) scale(1.15); opacity: 0.15; }
          100% { transform: translate(-50%, -50%) scale(0.85); opacity: 0.55; }
        }
        @keyframes splash-fill {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default AuthSuccessSplash;
